import { useState, useEffect } from "react";

import { INDUSTRY_NAICS_CODES } from "constant/projectConstant";

/**
 * Custom hook that ensures exact matching between filter results count and NAICS codes count
 * @param {string|Array} selectedIndustries - Selected industry category (string) or categories (array)
 * @param {Array} data - The full dataset of industry information
 * @returns {Array} - Filtered array of industry data matching the selected category
 */
const useExactNaicsFilter = (selectedIndustries, data) => {
  const [filteredData, setFilteredData] = useState([]);

  useEffect(() => {
    // Early return if no data
    if (!data || data?.length === 0) {
      setFilteredData([]);
      return;
    }

    // Normalize selected industries to array
    const industriesArray =
      typeof selectedIndustries === "string"
        ? [selectedIndustries]
        : Array.isArray(selectedIndustries)
          ? selectedIndustries
          : [];

    // If no industries selected, return original data
    if (!industriesArray.length) {
      setFilteredData(data);
      return;
    }

    // Gather all NAICS codes for selected industries
    const relevantCodes = industriesArray.flatMap(
      (industry) => INDUSTRY_NAICS_CODES[industry] || []
    );

    if (relevantCodes.length === 0) {
      setFilteredData(data);
      return;
    }

    // Create a Map of all data items indexed by their NAICS code
    const dataMap = new Map();
    data?.forEach((item) => {
      const codeFromLabel = item.label.split(":")[0].trim();
      if (relevantCodes.includes(codeFromLabel) && !dataMap.has(codeFromLabel)) {
        dataMap.set(codeFromLabel, item);
      }
    });

    // For Contractors, maintain the exact order of codes as defined
    if (industriesArray.length === 1 && industriesArray[0] === "Contractors") {
      const contractorsCodes = INDUSTRY_NAICS_CODES["Contractors"];
      const exactResults = contractorsCodes
        .map((code) => dataMap.get(code))
        .filter((item) => item !== undefined);
      
      setFilteredData(exactResults);
    } else {
      // For other industries, convert Map values to array
      const uniqueFiltered = Array.from(dataMap.values());
      setFilteredData(uniqueFiltered);
    }
  }, [selectedIndustries, data]);

  return filteredData;
};

export default useExactNaicsFilter;